export type TaiwanLandlineAreaCode = {
  code: string
  label: string
}

export const taiwanLandlineAreaCodes: TaiwanLandlineAreaCode[] = [
  { code: '02', label: '02 台北 / 新北 / 基隆' },
  { code: '03', label: '03 桃園 / 新竹 / 宜蘭 / 花蓮' },
  { code: '037', label: '037 苗栗' },
  { code: '04', label: '04 台中 / 彰化' },
  { code: '049', label: '049 南投' },
  { code: '05', label: '05 雲林 / 嘉義' },
  { code: '06', label: '06 台南 / 澎湖' },
  { code: '07', label: '07 高雄' },
  { code: '08', label: '08 屏東' },
  { code: '082', label: '082 金門 / 烏坵' },
  { code: '0836', label: '0836 馬祖' },
  { code: '089', label: '089 台東' },
]

const areaCodesBySpecificity = [...taiwanLandlineAreaCodes].sort(
  (left, right) => right.code.length - left.code.length,
)

function findPrefixedTaiwanLandlineAreaCode(value: string) {
  return areaCodesBySpecificity.find((areaCode) => value.startsWith(areaCode.code))
}

export function getDefaultTaiwanLandlineAreaCode(value: unknown) {
  if (
    typeof value === 'string' &&
    taiwanLandlineAreaCodes.some((areaCode) => areaCode.code === value)
  ) {
    return value
  }

  return '02'
}

export function formatTaiwanLandlineAnswer(value: string, fallbackAreaCode = '02') {
  const trimmedValue = value.trim().replace(/\s+/g, ' ')
  if (!trimmedValue) return ''

  const localAreaCode = findPrefixedTaiwanLandlineAreaCode(trimmedValue)
  if (localAreaCode) {
    const number = trimmedValue
      .slice(localAreaCode.code.length)
      .replace(/^[\s-]+/, '')
      .trim()
    return number ? `${localAreaCode.code}-${number}` : localAreaCode.code
  }

  const internationalMatch = trimmedValue.match(/^\+886[\s-]*(\d{1,4})(?:[\s-]+(.+))?$/)
  if (!internationalMatch) return trimmedValue

  const trunklessAreaCode = internationalMatch[1]
  const number = internationalMatch[2]?.trim().replace(/\s+/g, ' ') ?? ''
  const localCode = `0${trunklessAreaCode}`
  const isKnownLandline = taiwanLandlineAreaCodes.some((areaCode) => areaCode.code === localCode)
  if (!isKnownLandline) return trimmedValue

  return number ? `${localCode}-${number}` : fallbackAreaCode
}

function splitTaiwanLandlineExtension(value: string) {
  const match = value.match(/(?:[-\s]*(?:#|分機|轉|ext\.?|x)\s?)(\d{1,6})$/i)
  if (!match) return { number: value.trim(), extension: '' }

  return {
    number: value.slice(0, match.index).trim(),
    extension: match[1],
  }
}

export function parseTaiwanLandlineValue(value: string | undefined, fallbackAreaCode = '02') {
  const formattedValue = value ? formatTaiwanLandlineAnswer(value, fallbackAreaCode) : ''
  const areaCode = findPrefixedTaiwanLandlineAreaCode(formattedValue)

  if (!formattedValue) return { areaCode: fallbackAreaCode, number: '', extension: '' }
  if (!areaCode) {
    const parsed = splitTaiwanLandlineExtension(formattedValue)
    return { areaCode: fallbackAreaCode, number: parsed.number, extension: parsed.extension }
  }

  const parsed = splitTaiwanLandlineExtension(
    formattedValue
      .slice(areaCode.code.length)
      .replace(/^[\s-]+/, '')
      .trim(),
  )
  return {
    areaCode: areaCode.code,
    number: parsed.number,
    extension: parsed.extension,
  }
}
